<template>
  <div class="admin-reports">
    <div
      v-if="!isAdmin"
      class="text-center text-muted py-3"
    >
      No tienes permisos para ver este reporte.
    </div>

    <template v-else>
      <b-row class="align-items-center mb-2">
        <b-col>
          <h1 class="font-weight-bolder mb-0">
            Clientes Semanal
          </h1>
          <small class="text-muted">
            {{ tableTitle }}
          </small>
        </b-col>

        <b-col
          cols="12"
          md="4"
          lg="3"
          class="mt-1 mt-md-0"
        >
          <div class="d-flex align-items-center">
            <b-form-input
              v-model.number="year"
              type="number"
              min="2020"
              step="1"
              class="mr-1"
              @keyup.enter="loadReport"
            />

            <b-button
              variant="primary"
              :disabled="loading"
              @click="loadReport"
            >
              Cargar
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-overlay
        :show="loading"
        opacity="0.12"
      >
        <b-alert
          v-if="errorMessage"
          show
          variant="danger"
          class="mb-2"
        >
          {{ errorMessage }}
        </b-alert>

        <b-card
          v-if="weeks.length"
          no-body
        >
          <div class="table-responsive">
            <table class="table table-sm mb-0 report-table">
              <thead>
                <tr class="month-row">
                  <th
                    class="text-left customer-col"
                    rowspan="2"
                  >
                    Cliente
                  </th>
                  <th
                    v-for="(group, index) in monthHeaderCells"
                    :key="`month-${index}`"
                    :colspan="group.span"
                    class="text-center"
                  >
                    {{ group.label }}
                  </th>
                  <th
                    class="text-center total-col"
                    rowspan="2"
                  >
                    Total
                  </th>
                </tr>

                <tr>
                  <th
                    v-for="week in weeks"
                    :key="`week-header-${week.index}`"
                    class="text-center week-col"
                    :title="week.label || ''"
                  >
                    {{ weekHeader(week) }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(customer, rowIndex) in customers"
                  :key="rowKey(customer, rowIndex)"
                >
                  <td class="text-left customer-col">
                    {{ customer.customer_name || '-' }}
                  </td>

                  <td
                    v-for="week in weeks"
                    :key="`${rowKey(customer, rowIndex)}-week-${week.index}`"
                    class="text-center"
                    :class="cellClass(customer, week.index)"
                  >
                    {{ valueFor(customer, week.index) }}
                  </td>

                  <td class="text-center font-weight-bold total-col">
                    {{ rowTotal(customer) }}
                  </td>
                </tr>

                <tr v-if="!customers.length">
                  <td
                    class="text-center text-muted py-2"
                    :colspan="weeks.length + 2"
                  >
                    No hay clientes con compras para el periodo seleccionado.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <b-alert
          v-else-if="!loading"
          show
          variant="light"
        >
          No hay datos para el año seleccionado.
        </b-alert>
      </b-overlay>
    </template>
  </div>
</template>

<script>
import axios from '@axios'

export default {
  name: 'AdminReportsCustomerWeekly',
  data() {
    return {
      loading: false,
      year: 2026,
      errorMessage: '',
      reportData: null,
    }
  },
  computed: {
    reportPayload() {
      const raw = this.reportData
      if (!raw || typeof raw !== 'object') return {}

      const hasTopLevelFields = ['weeks', 'matrix', 'customers', 'rows']
        .some(key => Object.prototype.hasOwnProperty.call(raw, key))

      if (hasTopLevelFields) return raw
      if (raw.data && typeof raw.data === 'object') return raw.data
      return raw
    },
    isAdmin() {
      const user = this.$store.getters['auth/currentUser'] || {}
      return user.role_name === 'admin'
    },
    weeks() {
      const weeks = this.reportPayload?.weeks
      return Array.isArray(weeks) ? weeks : []
    },
    customerRowsRaw() {
      const payload = this.reportPayload || {}
      const rows = payload.matrix || payload.customers || payload.rows || payload.report || []

      if (Array.isArray(rows)) return rows
      if (typeof rows === 'string') {
        try {
          const parsed = JSON.parse(rows)
          if (Array.isArray(parsed)) return parsed
        } catch (error) {
          return []
        }
      }
      if (rows && typeof rows === 'object') return Object.values(rows)

      const fallbackArray = Object.values(payload).find(value => (
        Array.isArray(value)
        && value.length
        && typeof value[0] === 'object'
        && (
          Object.prototype.hasOwnProperty.call(value[0], 'customer_name')
          || Object.prototype.hasOwnProperty.call(value[0], 'customer_email')
          || Object.prototype.hasOwnProperty.call(value[0], 'customer_id')
          || Object.prototype.hasOwnProperty.call(value[0], 'weeks')
        )
      ))
      if (Array.isArray(fallbackArray)) return fallbackArray

      return []
    },
    customers() {
      const rows = this.customerRowsRaw
        .map(row => this.normalizeCustomer(row))
        .filter(row => row !== null)

      return rows
        .slice()
        .sort((a, b) => Number(b?.total_general || 0) - Number(a?.total_general || 0))
    },
    monthHeaderCells() {
      const cells = []

      this.weeks.forEach(week => {
        const label = this.monthNameFromWeek(week)
        const prev = cells[cells.length - 1]

        if (!prev || prev.label !== label) {
          cells.push({ label, span: 1 })
          return
        }

        prev.span += 1
      })

      return cells
    },
    periodLabel() {
      const labels = this.monthHeaderCells.map(group => group.label)
      if (!labels.length) return 'Año completo'
      if (labels.length === 1) return labels[0]
      if (labels.length === 2) return `${labels[0]} y ${labels[1]}`
      return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`
    },
    weekTotals() {
      return this.weeks.reduce((acc, week) => {
        const total = this.customers.reduce((sum, customer) => sum + this.valueFor(customer, week.index), 0)
        acc[week.index] = total
        return acc
      }, {})
    },
    tableTitle() {
      const selectedYear = Number(this.reportPayload?.year || this.year || 2026)
      return `TABLA: Compradas por cliente - ${this.periodLabel} (semanas) - ${selectedYear}`
    },
  },
  created() {
    if (this.isAdmin) {
      this.loadReport()
    }
  },
  methods: {
    rowKey(customer, index) {
      const key = customer?.customer_id || customer?.customer_email || customer?.customer_name || 'customer'
      return `${key}-${index}`
    },
    normalizeCustomer(row) {
      if (!row || typeof row !== 'object') return null

      const nestedCustomer = (row.customer && typeof row.customer === 'object') ? row.customer : {}
      const rowWeeks = Array.isArray(row.weeks) ? row.weeks : []
      const objectWeeks = (!rowWeeks.length && row.weeks && typeof row.weeks === 'object')
        ? Object.keys(row.weeks).map(weekKey => ({
          week: Number(weekKey),
          purchased: Number(row.weeks[weekKey] || 0),
        }))
        : []

      const weeksSource = rowWeeks.length ? rowWeeks : objectWeeks
      const normalizedWeeks = weeksSource.map(item => ({
        week: Number(item?.week || item?.index || item?.week_index || 0),
        purchased: Number(item?.purchased || item?.value || item?.total || 0),
      }))

      const totalByWeeks = normalizedWeeks.reduce((sum, item) => sum + Number(item.purchased || 0), 0)

      return {
        customer_id: row.customer_id || row.id || nestedCustomer.id || '',
        customer_name: row.customer_name || row.name || nestedCustomer.name || '-',
        customer_email: row.customer_email || row.email || nestedCustomer.email || '-',
        customer_phone: row.customer_phone || row.phone || nestedCustomer.phone || '-',
        weeks: normalizedWeeks,
        total_general: Number(row.total_general || row.total || row.total_purchased || totalByWeeks),
      }
    },
    monthNameFromWeek(week) {
      if (!week?.from) return 'Mes'
      const date = new Date(`${week.from}T00:00:00`)
      const month = date.toLocaleDateString('es-MX', { month: 'long' })
      return month.charAt(0).toUpperCase() + month.slice(1)
    },
    weekHeader(week) {
      const weekIndex = Number(week?.index || 0)
      const total = this.weekTotals[week?.index] || 0
      if (!weekIndex) return week?.label || '-'
      return `Semana ${weekIndex} (${total})`
    },
    valueFor(customer, weekIndex) {
      if (!customer || !Array.isArray(customer.weeks)) return 0
      const found = customer.weeks.find(item => Number(item.week) === Number(weekIndex))
      return Number(found?.purchased || 0)
    },
    rowTotal(customer) {
      return Number(customer?.total_general || 0)
    },
    cellClass(customer, weekIndex) {
      const currentValue = Number(this.valueFor(customer, weekIndex) || 0)
      const currentPosition = this.weeks.findIndex(week => Number(week.index) === Number(weekIndex))
      const previousWeek = currentPosition > 0 ? this.weeks[currentPosition - 1] : null
      const previousValue = previousWeek ? Number(this.valueFor(customer, previousWeek.index) || 0) : 0
      const hasPositiveBeforeCurrent = currentPosition > 0
        ? this.weeks
          .slice(0, currentPosition)
          .some(week => Number(this.valueFor(customer, week.index) || 0) > 0)
        : false

      if (previousValue === 0 && currentValue > 0) return 'cell-green'
      if (previousValue > 0 && currentValue > 0) return 'cell-blue'
      if (previousValue > 0 && currentValue === 0) return 'cell-red'
      if (previousValue === 0 && currentValue === 0 && hasPositiveBeforeCurrent) return 'cell-red'
      return 'cell-zero'
    },
    async loadReport() {
      this.loading = true
      this.errorMessage = ''

      try {
        const params = {
          year: Number(this.year || 2026),
        }

        const { data } = await axios.get('/v1/reports/quotes_purchased_by_customer_weekly_january', {
          params,
        })

        this.reportData = data || {}
      } catch (error) {
        const responseData = error?.response?.data || {}
        this.errorMessage = responseData.message || responseData.error || 'No se pudo cargar el reporte semanal por cliente'
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-reports {
  display: flex;
  flex-direction: column;
}

.report-table {
  color: #111;

  th,
  td {
    vertical-align: middle;
    border: 1px solid #bfbfbf;
    white-space: nowrap;
    padding: 0.3rem 0.55rem;
    line-height: 1.2;
  }

  th {
    font-weight: 700;
    background-color: #fff;
  }

  th.customer-col {
    position: sticky;
    left: 0;
    z-index: 6;
    background: #fff;
    box-shadow: 1px 0 0 #bfbfbf;
  }

  td.customer-col {
    position: sticky;
    left: 0;
    z-index: 3;
    background: #fff;
    box-shadow: 1px 0 0 #bfbfbf;
  }
}

.month-row {
  th {
    background: #fff;
    text-transform: uppercase;
    font-size: 0.78rem;
    color: #6f6f78;
    letter-spacing: 0.02em;
  }
}

.customer-col {
  min-width: 180px;
}

.week-col {
  min-width: 160px;
}

.total-col {
  min-width: 100px;
}

.cell-zero {
  background-color: #d6d6d6;
}

.cell-green {
  background-color: #b7d7a8;
}

.cell-blue {
  background-color: #56c1cf;
}

.cell-red {
  background-color: #df9a9a;
}
</style>
